import React, { useState } from 'react'
import { ListMenu, ListMenuButton, Spacer, Text, View, useWindowResizeSensor } from 'oio-react'
import PropTypes from 'prop-types'
import AccountIcon from 'assets/icons/account'
import CloseIcon from 'assets/icons/close'
import { useMe, useOrganization } from 'src/core/graphql/hooks'
import AboutMe from './AboutMe'
import DeleteAccount from './DeleteAccount'
import GitHubAccount from './GitHubAccount'
import PasswordSecurity from './PasswordSecurity'
import Privacy from './Privacy'
import ProfileInformation from './ProfileInformation'

// We use constants instead of routes so that the account settings component
// can be used in contexts that are both route and non-route based
const ACCOUNT_SETTINGS_SUBVIEW_1 = 'profile information'
const ACCOUNT_SETTINGS_SUBVIEW_2 = 'about me'
const ACCOUNT_SETTINGS_SUBVIEW_3 = 'security'
const ACCOUNT_SETTINGS_SUBVIEW_4 = 'privacy'
const ACCOUNT_SETTINGS_SUBVIEW_5 = 'github'
const ACCOUNT_SETTINGS_SUBVIEW_6 = 'delete'

const AccountSettings = ({ onCloseButtonClick }) => {
   const { me } = useMe()
   const { organization } = useOrganization()
   const { currentBreakpoint } = useWindowResizeSensor()
   const defaultSubview = currentBreakpoint > 'b' && ACCOUNT_SETTINGS_SUBVIEW_1
   const [subview, setSubview] = useState(defaultSubview)

   // For now, only show Github Integration tab in GNS3 for moderators
   const gns3ModeratorBadgeId = process.env.NODE_ENV === 'development'
      ? '61035e696af322f228f8b798'
      : '602c2c06081483369d479865'

   const isGNS3Moderator = me.badges.some(b => b.id === gns3ModeratorBadgeId)

   return (
      <View
         position="relative"
         borderRadius="5px[b-f]"
         style={{ overflow: 'hidden' }}
         float="left"
         width="100%"
         height="100%">
         <View
            float="left"
            width="100%[a-b] 240px[c-f]"
            height="100%"
            borderBottom="1px solid #eee[a-b]"
            borderRight="1px solid #eee[c-f]">
            <View
               borderBottom="1px solid #eee"
               padding="18px[a-b] 35px 20px[c-f]">
               <View
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="36px"
                  height="36px"
                  borderRadius="50%"
                  backgroundColor="#000">
                  <AccountIcon
                     width="20px"
                     height="20px"
                     strokeWidth="2px"
                     color="#fff"
                  />
               </View>
               <Spacer size="2" />
               <Text
                  size="5"
                  color="#000"
                  weight="medium">
                  Profile & Account Settings
               </Text>
               <Spacer size="2" />
               <Text size="2" color="#777">
                  {'Edit your profile information and account settings'}
               </Text>
            </View>
            <ListMenu buttonPaddingHorizontal="20px">
               <ListMenuButton
                  onClick={() => setSubview(ACCOUNT_SETTINGS_SUBVIEW_1)}
                  name="Profile Information"
                  isActive={subview === ACCOUNT_SETTINGS_SUBVIEW_1}
               />
               <ListMenuButton
                  onClick={() => setSubview(ACCOUNT_SETTINGS_SUBVIEW_2)}
                  name="About Me"
                  isActive={subview === ACCOUNT_SETTINGS_SUBVIEW_2}
               />
               <ListMenuButton
                  onClick={() => setSubview(ACCOUNT_SETTINGS_SUBVIEW_3)}
                  name="Password & Security"
                  isActive={subview === ACCOUNT_SETTINGS_SUBVIEW_3}
               />
               <ListMenuButton
                  onClick={() => setSubview(ACCOUNT_SETTINGS_SUBVIEW_4)}
                  name="Privacy Options"
                  isActive={subview === ACCOUNT_SETTINGS_SUBVIEW_4}
               />
               {organization.slug === 'gns3' &&
               (isGNS3Moderator || organization.currentUserIsAdmin) && (
                  <ListMenuButton
                     onClick={() => setSubview(ACCOUNT_SETTINGS_SUBVIEW_5)}
                     name="GitHub Account"
                     isActive={subview === ACCOUNT_SETTINGS_SUBVIEW_5}
                  />
               )}
               <ListMenuButton
                  onClick={() => setSubview(ACCOUNT_SETTINGS_SUBVIEW_6)}
                  name="Delete Account"
                  isActive={subview === ACCOUNT_SETTINGS_SUBVIEW_6}
               />
            </ListMenu>
         </View>
         <View
            display={subview ? 'block' : 'none'}
            position="absolute[a-b] relative[c-f]"
            top="0px"
            left="0px"
            float="left"
            width="100%[a-b] calc(100% - 240px)[c-f]"
            height="100%"
            backgroundColor="#fff"
            scroll="on">
            {subview === ACCOUNT_SETTINGS_SUBVIEW_1 && (
               <ProfileInformation onReturnButtonClick={() => setSubview(null)} />
            )}
            {subview === ACCOUNT_SETTINGS_SUBVIEW_2 && (
               <AboutMe onReturnButtonClick={() => setSubview(null)} />
            )}
            {subview === ACCOUNT_SETTINGS_SUBVIEW_3 && (
               <PasswordSecurity onReturnButtonClick={() => setSubview(null)} />
            )}
            {subview === ACCOUNT_SETTINGS_SUBVIEW_4 && (
               <Privacy onReturnButtonClick={() => setSubview(null)} />
            )}
            {subview === ACCOUNT_SETTINGS_SUBVIEW_5 && (
               <GitHubAccount onReturnButtonClick={() => setSubview(null)} />
            )}
            {subview === ACCOUNT_SETTINGS_SUBVIEW_6 && (
               <DeleteAccount onReturnButtonClick={() => setSubview(null)} />
            )}
         </View>
         <View
            position="absolute"
            right="0px"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            height="60px"
            padding="0px 15px"
            zIndex="2">
            <View
               display="flex"
               justifyContent="flex-end"
               alignItems="center"
               onClick={onCloseButtonClick}>
               <CloseIcon width="24px" height="24px" />
            </View>
         </View>
      </View>
   )
}

AccountSettings.propTypes = {
   onCloseButtonClick: PropTypes.func.isRequired
}

export default AccountSettings
